import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
// react-markdown + remark-gfm (~110 KB raw) used to ride the entry chunk
// because this widget is always mounted. Load the renderer the first time an
// assistant reply has to be drawn; until then the raw text shows.
const MarkdownBody = lazy(() => import('./MarkdownBody'));
import { HelpCircle, X, Send } from 'lucide-react';
import { streamHelpChat, fetchHelpConfig } from './helpAiClient';

const DEFAULT_WIDGET = { width: 360, height: 512 };

export default function HelpAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [config, setConfig] = useState(null); // null = loading; {enabled, available, widget}
  const scrollRef = useRef(null);

  useEffect(() => {
    // `available: true` on the fallback keeps a config-fetch failure from
    // hiding a working assistant; the real gate is the server's answer.
    fetchHelpConfig().then((c) => setConfig(c ?? { enabled: true, available: true, widget: DEFAULT_WIDGET }));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const next = [...messages, { role: 'user', content: text }];
    setMessages([...next, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      await streamHelpChat(next, (delta) => {
        setMessages((cur) => {
          const copy = cur.slice();
          copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + delta };
          return copy;
        });
      });
    } catch (e) {
      const msg = e.status === 503
        ? 'The help assistant is unavailable right now.'
        : e.status === 429
          ? 'You are sending messages too fast — please wait a moment.'
          : 'Sorry, something went wrong. Please try again.';
      setMessages((cur) => {
        const copy = cur.slice();
        copy[copy.length - 1] = { role: 'assistant', content: msg };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }

  // Don't render while loading, when the sultan has disabled it, or when this
  // box has no provider key — a bubble that can only answer "unavailable" is
  // worse than no bubble. `available` is undefined on a server older than the
  // flag, so treat only an explicit `false` as unavailable rather than hiding
  // the widget everywhere during a rollout.
  if (!config || !config.enabled || config.available === false) return null;

  const widget = config.widget || DEFAULT_WIDGET;

  const panel = (
    <div className="fixed bottom-5 end-5 z-[1000] flex flex-col items-end gap-3">
      {open && (
        <div
          style={{ width: widget.width, height: widget.height }}
          className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Help Assistant</div>
            <div className="text-[11px] text-slate-500">Answers from the help docs. Don't paste passwords or customer personal data.</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-sm text-slate-400">Ask me how to do something — e.g. "How do I add a vehicle?"</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'text-end' : 'text-start'}>
                <div className={`inline-block rounded-xl px-3 py-2 text-sm max-w-[85%] ${m.role === 'user' ? 'bg-sky-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100'}`}>
                  {m.role === 'assistant'
                    ? <Suspense fallback={<span className="whitespace-pre-wrap">{m.content || '…'}</span>}><MarkdownBody>{m.content || '…'}</MarkdownBody></Suspense>
                    : m.content}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Ask a question…"
              className="flex-1 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent px-3 py-2 outline-none"
            />
            <button onClick={send} disabled={busy} className="rounded-lg bg-sky-600 text-white p-2 disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Help assistant"
        className="rounded-full bg-sky-600 text-white w-12 h-12 flex items-center justify-center shadow-lg hover:bg-sky-700"
      >
        {open ? <X size={20} /> : <HelpCircle size={22} />}
      </button>
    </div>
  );

  return createPortal(panel, document.body);
}
